import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { fieldErrorsOf } from '@/api/client';
import { useCreateClientOrg } from '@/api/hooks';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { clientFieldOf, deliveryToast, slugFromName, validateClient, type ClientValues } from '@/features/members/membersModel';
import { providersNotConfigured, setupSucceeded, setupSummary } from './clientProvidersModel';

const EMPTY: ClientValues = { name: '', slug: '', adminEmail: '', adminName: '' };

/** A new client org and the invitation of its first admin (platform operators). */
export function CreateClientDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="clients-create-open" className="max-sm:h-11">
          <Icon name="add_business" size={18} />
          New client
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="clients-create-dialog" className="sm:max-w-lg" aria-describedby="clients-create-desc">
        {open ? <CreateClientForm onDone={() => setOpen(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function useClientForm() {
  const [values, setValues] = useState<ClientValues>(EMPTY);
  const [slugEdited, setSlugEdited] = useState(false);
  const [touched, setTouched] = useState(false);
  const [server, setServer] = useState<Partial<Record<keyof ClientValues, string>>>({});
  const local = validateClient(values);
  const errors = touched ? { ...server, ...local } : server;
  const set = (patch: Partial<ClientValues>) => {
    setServer({});
    setValues((v) => {
      const next = { ...v, ...patch };
      return patch.name !== undefined && !slugEdited ? { ...next, slug: slugFromName(patch.name) } : next;
    });
  };
  const serverFailed = (err: unknown) => {
    const mapped: Partial<Record<keyof ClientValues, string>> = {};
    for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
      const target = clientFieldOf(field);
      if (target) mapped[target] = message;
    }
    setServer(mapped);
  };
  return { values, errors, local, set, setSlugEdited, setTouched, serverFailed, hasServerErrors: Object.keys(server).length > 0 };
}

function CreateClientForm({ onDone }: { onDone: () => void }) {
  const form = useClientForm();
  const create = useCreateClientOrg();
  const navigate = useNavigate();
  const { values, errors, set } = form;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    form.setTouched(true);
    if (Object.keys(form.local).length) return;
    try {
      const created = await create.mutateAsync({ name: values.name.trim(), slug: values.slug, firstAdmin: { email: values.adminEmail.trim().toLowerCase(), ...(values.adminName.trim() ? { name: values.adminName.trim() } : {}) } });
      const providers = created.providers ? ` Providers: ${setupSummary(created.providers)}.` : '';
      const sent = `${deliveryToast(created.invitation).title}.`;
      if (created.invitation.emailStatus === 'failed') toast.warning(`${created.org.name} created, but the invitation email did not go out`, { description: `Ask its first admin to sign in with the invited email: the invitation is accepted then.${providers}` });
      else if (created.providers && !setupSucceeded(created.providers)) toast.warning(`${created.org.name} created; its providers are not fully set up`, { description: `${sent}${providers} Use Set up providers on its row to retry.` });
      else toast.success(`${created.org.name} created`, { description: `${sent}${providers}` });
      if (created.providers && providersNotConfigured(created.providers)) toast.info('Malible\'s Expo or Bitrise token is not set', { description: 'Set it in Hangar administration → Expo & Bitrise, then use Set up providers on the client\'s row.', action: { label: 'Set tokens', onClick: () => void navigate({ to: '/platform-providers' }) } });
      onDone();
    } catch (err) {
      form.serverFailed(err);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} noValidate className="flex min-h-0 flex-col">
      <DialogHeader>
        <DialogTitle>New client</DialogTitle>
        <DialogDescription id="clients-create-desc">Creates the organization, sets up its Expo and Bitrise accounts, and emails its first admin how to sign in. You do not become a member of it.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <FormField id="client-name" label="Client name" required error={errors.name}>
          <Input data-testid="client-name" autoFocus value={values.name} onChange={(e) => set({ name: e.target.value })} placeholder="Acme Mobile" {...fieldA11y('client-name', errors.name)} />
        </FormField>
        <FormField id="client-slug" label="Slug" required error={errors.slug} hint="Lowercase letters, digits and hyphens; it cannot change later">
          <Input data-testid="client-slug" value={values.slug} onChange={(e) => { form.setSlugEdited(true); set({ slug: e.target.value.toLowerCase() }); }} className="font-mono" {...fieldA11y('client-slug', errors.slug, 'hint')} />
        </FormField>
        <FormField id="client-admin-email" label="First admin's email" required error={errors.adminEmail} hint="Its domain becomes the organization's domain: people there join as members, everyone else as external guests">
          <Input data-testid="client-admin-email" type="email" autoComplete="off" value={values.adminEmail} onChange={(e) => set({ adminEmail: e.target.value })} placeholder="it-admin@client.com" {...fieldA11y('client-admin-email', errors.adminEmail, 'hint')} />
        </FormField>
        <FormField id="client-admin-name" label="First admin's name" hint="Optional: used in the email">
          <Input data-testid="client-admin-name" value={values.adminName} onChange={(e) => set({ adminName: e.target.value })} {...fieldA11y('client-admin-name', undefined, 'hint')} />
        </FormField>
        {create.error && !form.hasServerErrors ? <ProblemAlert error={create.error} testId="clients-create-error" /> : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} className="max-sm:h-11">Cancel</Button>
        <Button type="submit" data-testid="clients-create-submit" disabled={create.isPending} className="max-sm:h-11">
          {create.isPending ? 'Creating…' : 'Create and invite'}
        </Button>
      </DialogFooter>
    </form>
  );
}
